import { getAddress, type Hex } from 'viem';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import { walletWagmiConfig } from '@/features/wallet';
import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import type { PasskeyAssociationResponse } from '@/lib/api/tradingSession';

const custodialWalletAbi = [
    {
        name: 'addAccountHash',
        type: 'function',
        stateMutability: 'nonpayable',
        inputs: [
            { name: '_hashes', type: 'bytes32[]' },
            { name: '_signer', type: 'address' },
            { name: '_issued', type: 'uint256' },
            { name: '_expiry', type: 'uint256' },
            { name: '_digest', type: 'bytes32' },
            { name: '_sig', type: 'bytes' },
        ],
        outputs: [],
    },
    {
        name: 'removeAccountHash',
        type: 'function',
        stateMutability: 'nonpayable',
        inputs: [{ name: '_hashes', type: 'bytes32[]' }],
        outputs: [],
    },
] as const;

function bytes32(value: string): Hex {
    if (!/^0x[0-9a-fA-F]{64}$/u.test(value)) {
        throw new Error('Expected a 0x-prefixed 32-byte account hash.');
    }
    return value as Hex;
}

function associationSignature(value: string): Hex {
    if (!/^0x[0-9a-fA-F]{130}$/u.test(value)) {
        throw new Error(
            'Expected an exact 65-byte signature for passkey association.',
        );
    }
    return value as Hex;
}

async function requireSuccessfulReceipt(
    transactionHash: Hex,
    action: string,
    chainId: AnchorChainId,
): Promise<void> {
    const receipt = await waitForTransactionReceipt(walletWagmiConfig, {
        hash: transactionHash,
        chainId,
    });
    if (receipt.status !== 'success') {
        throw new Error(`${action} transaction reverted.`);
    }
}

export async function submitPasskeyAssociationTransaction(
    ownerInput: string,
    association: PasskeyAssociationResponse,
    onSubmitted?: () => void,
    chainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<Hex> {
    if (association.intent !== 'link') {
        throw new Error(
            'Passkeys can only be added to an existing authorization wallet.',
        );
    }

    const transactionHash = await writeContract(walletWagmiConfig, {
        account: getAddress(ownerInput),
        address: getAddress(association.subject),
        abi: custodialWalletAbi,
        functionName: 'addAccountHash',
        args: [
            [bytes32(association.accountHash)],
            getAddress(association.signer),
            BigInt(association.issued),
            BigInt(association.expiry),
            bytes32(association.digest),
            associationSignature(association.signature),
        ],
        chainId,
    });
    onSubmitted?.();
    await requireSuccessfulReceipt(
        transactionHash,
        'Passkey association',
        chainId,
    );
    return transactionHash;
}

export async function submitPasskeyRemovalTransaction(
    ownerInput: string,
    authorizationWalletInput: string,
    accountHashInput: string,
    chainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<Hex> {
    const transactionHash = await writeContract(walletWagmiConfig, {
        account: getAddress(ownerInput),
        address: getAddress(authorizationWalletInput),
        abi: custodialWalletAbi,
        functionName: 'removeAccountHash',
        args: [[bytes32(accountHashInput)]],
        chainId,
    });
    await requireSuccessfulReceipt(transactionHash, 'Passkey removal', chainId);
    return transactionHash;
}
